import { Link } from "react-router-dom";
import { ArrowUpRight, Award, MapPin, MessageCircle, Users } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LedTicker } from "../components/LedTicker";
import { SITE, SISTER_CONCERNS, IMG } from "../data/site";

export default function About() {
    return (
        <>
            <Seo
                title="About Morningstar Enterprises Kochi | LED & AV Since 2009"
                description="Morning Star Enterprises, Kochi — NSIC & MSME ZED certified LED display and AV technology company serving Kerala, Tamil Nadu & Karnataka since 2009."
                path="/about"
                keywords="Morningstar Enterprises Kochi, LED display company Kerala, about Morning Star Enterprises, NSIC MSME ZED certified"
            />

            <section className="led-dots spotlight border-b border-slate-800/70" data-testid="about-hero">
                <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
                    <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">
                        About Us
                    </p>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight sm:text-5xl lg:text-6xl">
                        Lighting up South India <span className="led-glow-amber">since 2009.</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 md:text-lg">
                        As a leading technology company, Morning Star Enterprises proudly claims
                        to have served thousands of clients across Kerala, Karnataka and Tamil
                        Nadu since its inception in 2009.
                    </p>
                </div>
                <LedTicker
                    messages={[
                        "Morning Star Enterprises · Kochi",
                        "NSIC & MSME ZED Certified",
                        "SME Chamber of India member",
                        "Cochin Chamber of Commerce & Industry member",
                    ]}
                />
            </section>

            <section className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2" data-testid="about-story">
                <Reveal>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        What we make and do
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">
                        Some of our best sellers are scrolling LED boards, interactive flat
                        panel displays, interactive boards, LED screens and displays, touch
                        monitors, digital multimedia podiums, online classroom solutions, video
                        conference cameras, speakers, conference microphones, and digital
                        signage solutions.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">
                        Morning Star Enterprises is an NSIC and MSME ZED certified company with
                        several years of experience in manufacturing technology products, and a
                        proud member of the SME Chamber of India and The Cochin Chamber of
                        Commerce &amp; Industry.
                    </p>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">
                        We also provide consultancy services on technology solutions for
                        teaching, training, broadcasting, signage and conferencing.
                    </p>
                    <Link
                        to="/products/scrolling-led-boards"
                        data-testid="about-flagship-link"
                        className="mt-6 inline-flex items-center gap-2 font-semibold text-[#FF9900]"
                    >
                        Start with our flagship scrolling LED boards
                    </Link>
                </Reveal>
                <Reveal delay={0.12}>
                    <img
                        src={IMG.outdoorLed}
                        alt="Outdoor LED screen installation by Morningstar Enterprises Kochi"
                        loading="lazy"
                        className="w-full rounded-2xl border border-slate-800 object-cover"
                    />
                </Reveal>
            </section>

            <section className="border-y border-slate-800/70 bg-[#0C0E16]" data-testid="about-credentials">
                <div className="mx-auto grid max-w-7xl gap-4 px-4 py-16 sm:px-6 md:grid-cols-3">
                    {[
                        { icon: Award, t: "NSIC & MSME ZED Certified", d: "Certified manufacturing credentials with years of technology product experience." },
                        { icon: Users, t: "Chamber memberships", d: "Proud member of the SME Chamber of India and The Cochin Chamber of Commerce & Industry." },
                        { icon: MapPin, t: "Three states, one team", d: "Sales, installation and service across Kerala, Tamil Nadu and Karnataka from our Kochi base." },
                    ].map((c, i) => (
                        <Reveal key={c.t} delay={i * 0.08}>
                            <article className="h-full rounded-2xl border border-slate-800 bg-[#12141E] p-6">
                                <c.icon className="h-7 w-7 text-[#FF9900]" aria-hidden="true" />
                                <h3 className="mt-4 font-display text-xl font-semibold uppercase tracking-wide">{c.t}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{c.d}</p>
                            </article>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6" data-testid="sister-concerns">
                <Reveal>
                    <p className="font-led text-lg uppercase tracking-[0.35em] text-[#00A8FF]">
                        Our Family of Companies
                    </p>
                    <h2 className="mt-3 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        Sister concerns
                    </h2>
                    <p className="mt-3 max-w-2xl text-base text-slate-400">
                        The Morningstar group extends beyond displays — into lighting,
                        technology services and hospitality across Kerala.
                    </p>
                </Reveal>
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {SISTER_CONCERNS.map((s, i) => (
                        <Reveal key={s.name} delay={i * 0.08}>
                            <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`sister-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                className="group block h-full rounded-2xl border border-slate-800 bg-[#12141E] p-6 transition-colors hover:border-[#FF9900]/40"
                            >
                                <span className="inline-flex items-center gap-2 font-display text-xl font-semibold uppercase tracking-wide text-slate-100">
                                    {s.name}
                                    <ArrowUpRight className="h-4 w-4 text-[#FF9900] transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                                </span>
                                <span className="mt-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#FF9900]">
                                    <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> {s.location}
                                </span>
                                <span className="mt-2 block text-sm text-slate-400">{s.desc}</span>
                            </a>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6" data-testid="about-cta">
                <Reveal>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                        Meet us at Palarivattom, Kochi
                    </h2>
                    <p className="mx-auto mt-3 max-w-xl text-base text-slate-400">{SITE.address}</p>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                        <a
                            href={SITE.whatsapp}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="about-whatsapp-cta"
                            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 font-semibold text-black shadow-[0_0_18px_rgba(37,211,102,0.45)] transition-transform hover:scale-105"
                        >
                            <MessageCircle className="h-5 w-5" /> WhatsApp {SITE.phoneDisplay}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="about-contact-link"
                            className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-6 py-3 font-semibold text-slate-200 transition-colors hover:border-[#FF9900] hover:text-[#FF9900]"
                        >
                            Contact details &amp; map
                        </Link>
                    </div>
                </Reveal>
            </section>
        </>
    );
}
